import { RequestStatus } from 'features/request/requestSlice';
import type { useRequestParams } from 'features/request/useRequest';
import { useRequest } from 'features/request/useRequest';
import type { AnyObject } from 'types/AnyObject';

import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

export const getHasNextPageValue = (dataObj: AnyObject) => {
    const dataKey = Object.keys(dataObj ?? {})?.[0];

    const data = dataObj?.[dataKey as keyof typeof dataObj] as unknown as
        | {
              pageInfo?: {
                  hasNextPage?: boolean;
              };
          }
        | undefined;

    return data?.pageInfo?.hasNextPage;
};

export const getKeyAndItemsFromData = (
    dataObj: any,
    dataObjModifier?: (items: any[]) => any,
): { items: any[]; key: string } => {
    const sourceDataKey = Object.keys(dataObj ?? {})?.[0];
    const source = dataObj?.[sourceDataKey as keyof typeof dataObj] as unknown as { items?: AnyObject[] } | undefined;

    if (source && 'items' in source && source.items) {
        return { items: dataObjModifier ? dataObjModifier(source.items) : source.items, key: sourceDataKey };
    }

    return {
        items: [],
        key: sourceDataKey,
    };
};

export const useFetchAllData = ({ requestId, query, variables, withEffect = true }: useRequestParams) => {
    const dispatch = useDispatch();

    const [isAllFetchedFlag, setIsAllFetchedFlag] = useState(false);
    const [skipVariable, setSkipVariable] = useState(0);
    const [tempDataStorage, setTempDataStorage] = useState<AnyObject[]>([]);

    const requestVariables = useMemo<AnyObject>(
        () => ({
            ...variables,
            skip: skipVariable,
        }),
        [skipVariable, variables],
    );

    const [data, status, , , setNewData] = useRequest({
        requestId,
        query,
        variables: requestVariables,
        withEffect,
    });

    useEffect(() => {
        if (data && status === RequestStatus.SUCCESS) {
            const dataChunk = getKeyAndItemsFromData(data).items;

            if (getHasNextPageValue(data)) {
                setTempDataStorage((prevChunk) => [...prevChunk, ...dataChunk]);
                setSkipVariable((prevSkipVariable) => prevSkipVariable + dataChunk.length);
            } else if (!getHasNextPageValue(data) && !isAllFetchedFlag) {
                setTempDataStorage((prevChunk) => [...prevChunk, ...dataChunk]);
                setIsAllFetchedFlag(true);
            }
        }
    }, [data, isAllFetchedFlag, status]);

    useEffect(() => {
        if (isAllFetchedFlag && tempDataStorage.length) {
            dispatch(
                setNewData({
                    ...data,
                    [getKeyAndItemsFromData(data).key]: {
                        ...data[getKeyAndItemsFromData(data).key],
                        items: tempDataStorage,
                    },
                }),
            );
            setTempDataStorage([]);
        }
    }, [data, dispatch, isAllFetchedFlag, setNewData, tempDataStorage]);

    return useMemo(() => {
        const summaryData = isAllFetchedFlag ? data : undefined;

        return [summaryData, status];
    }, [isAllFetchedFlag, data, status]);
};
